<template>
    <GeneratingProgressBar
        v-if="generatingSuggestions"
        message="Generating suggestions"
    />
    <div v-if="suggestions.length" class="suggestions">
        <h4>Suggestions</h4>
        <div
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="suggestion-card"
            :class="{ selected: selectedSuggestions.includes(index) }"
            @click="toggleSuggestionSelection(index)"
            style="cursor: pointer"
        >
            <div class="indicator" v-if="selectedSuggestions.includes(index)">
                Selected
            </div>
            <div class="suggestion-content">
                <h3 v-if="!isEditing[index]">{{ suggestion.name }}</h3>
                <InputText
                    v-else
                    v-model="suggestion.name"
                    placeholder="Edit name"
                />
                <strong>Description</strong>
                <p v-if="!isEditing[index]">{{ suggestion.description }}</p>
                <Textarea
                    v-else
                    v-model="suggestion.description"
                    placeholder="Edit description"
                />
                <div class="actions">
                    <Button
                        icon="pi pi-pencil"
                        text
                        @click.stop="toggleEditMode(index)"
                        :label="isEditing[index] ? 'Save' : 'Edit'"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import GeneratingProgressBar from "@/components/GeneratingProgressBar.vue";

const props = defineProps<{
    generatingSuggestions: boolean;
    suggestions: { name: string | undefined; description: string }[];
    selectedSuggestions: number[];
}>();

const emit = defineEmits<{
    (e: "update:selectedSuggestions", value: number[]): void;
}>();

const isEditing = ref<boolean[]>([]); // Tracks edit mode for each suggestion

const toggleSuggestionSelection = (index: number) => {
    const selectedSuggestions = [...props.selectedSuggestions];
    const selectedIndex = props.selectedSuggestions.indexOf(index);
    if (selectedIndex > -1) {
        selectedSuggestions.splice(selectedIndex, 1);
    } else {
        selectedSuggestions.push(index);
    }
    emit("update:selectedSuggestions", selectedSuggestions);
};

const toggleEditMode = (index: number) => {
    isEditing.value[index] = !isEditing.value[index];
};
</script>

<style lang="scss" scoped>
.suggestions {
    display: flex;
    flex-direction: column;
    gap: 1em;
    margin-top: 1em;

    .suggestion-card {
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 1em;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: #fff;
        transition: box-shadow 0.3s;

        &:hover {
            box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
        }

        &.selected {
            border-color: #4c8bf5;
            background-color: #f0f7ff;
        }

        .suggestion-content {
            display: flex;
            flex-direction: column;
            gap: 0.5em;

            h5 {
                margin: 0;
                color: #333;
            }

            p {
                margin: 0;
                color: #666;
            }
        }
        .indicator {
            height: 0;
            text-align-last: right;
            color: #4c8bf5;
        }

        .actions {
            margin-top: 0.5em;
            align-self: flex-end;
        }
    }

    .empty-state {
        display: flex;
        justify-content: center;
        padding: 2em;
    }
}
</style>
